/**
 * Working out what somebody just uploaded, and preparing it.
 *
 * A sprite is enlarged; a photograph is shrunk. They are opposite problems and
 * the tool decides which it has rather than asking, because somebody uploading a
 * photo of a mug should not have to know the difference.
 *
 * None of this touches the DOM and none of it calls the service, which is what
 * lets the welcome page run the whole thing as a demonstration: drop an image,
 * see it classified and reduced, without spending a penny or needing an account.
 * The workbench then uses the identical result as the reference for a real draw.
 *
 * A 32 by 48 sprite is almost nothing for a model to look at, so it goes up with
 * nearest neighbour, which keeps every pixel a hard square. A photograph arrives
 * far too big and full of detail nobody wants copied, so it comes down with
 * smooth scaling, which is right here precisely because the pixels are not the
 * point.
 */

import type { Rgba } from "../src/raster.js";

export const UPLOAD_TARGET = 448;
export const MAX_UPLOAD_BYTES = 12_000_000;
/** Above this it is a photograph, not pixel art, whatever it is a picture of. */
export const SPRITE_MAX_EDGE = 512;
/** Pixel art is indexed. Thousands of colours means a camera was involved. */
export const SPRITE_MAX_COLOURS = 96;
/** Enough of a sprite's palette to rebuild its kit from. */
export const MAX_PALETTE = 48;
/** A photograph gets a hint, not a palette. Twelve is about what a sprite uses. */
export const PHOTO_PALETTE = 12;
/** How coarsely a photo's colours are rounded before counting. */
const BUCKET = 24;

export type ShotKind = "sprite" | "photo";

export interface Bitmap {
	width: number;
	height: number;
	data: Uint8Array;
}

export interface Shot {
	/** What goes to the service. */
	media_type: string;
	data: string;
	width: number;
	height: number;
	palette: string[];
	kind: ShotKind;

	/** Everything below is for showing a person what happened. */
	name: string;
	/** How many distinct colours were counted, capped at the threshold. */
	distinct: number;
	/** True if counting stopped at the cap rather than running out. */
	countCapped: boolean;
	/** Whole number for a sprite, a fraction for a photo. */
	scale: number;
	/** The image as the model will see it. */
	seen: Bitmap;
	/**
	 * A small copy for showing in a row of controls.
	 *
	 * Prepared here rather than left to the caller, because `seen` is the wrong
	 * source for it: a sprite has been enlarged to 448px and squeezing that back
	 * down to 24 in CSS reintroduces exactly the smearing the enlargement existed
	 * to avoid.
	 */
	thumb: Bitmap;
	/** The palette as colours rather than as text. */
	swatches: Rgba[];
}

/** Roughly this many pixels on the long edge, for a thumbnail. */
const THUMB = 48;

/**
 * The colours in the file, most used first.
 *
 * For a sprite these are exact and the model is told to build its kit from
 * them. For a photo they are only a hint, because a photograph's true palette
 * is thousands of near-identical values and copying them would defeat the point
 * of pixel art. Photos are rounded into buckets before counting, or the "top"
 * colours come back as forty shades of the same wall.
 */
function paletteOf(data: Uint8ClampedArray, exact: boolean): Rgba[] {
	const seen = new Map<string, { n: number; c: Rgba }>();
	for (let i = 0; i < data.length; i += 4) {
		if (data[i + 3] === 0) continue;
		const q = (v: number) => (exact ? v : Math.round(v / BUCKET) * BUCKET);
		const c: Rgba = [q(data[i]), q(data[i + 1]), q(data[i + 2]), exact ? data[i + 3] : 255];
		const key = c.join(",");
		const found = seen.get(key);
		if (found) found.n += 1;
		else seen.set(key, { n: 1, c });
	}
	return [...seen.values()]
		.sort((a, b) => b.n - a.n)
		.slice(0, exact ? MAX_PALETTE : PHOTO_PALETTE)
		.map((e) => e.c);
}

export const asText = (c: Rgba) => `[${c.join(",")}]`;

/**
 * Decide what an image is, and prepare it either way.
 *
 * Throws with a sentence worth showing a person. The caller owns saying it.
 */
export async function readShot(file: File): Promise<Shot> {
	if (file.size > MAX_UPLOAD_BYTES) {
		throw new Error(`That file is ${(file.size / 1_000_000).toFixed(1)}MB. Keep it under 12MB.`);
	}

	const bitmap = await createImageBitmap(file);
	const { width, height } = bitmap;

	// Read it at its own size first, so the colour count is the real one.
	const off = new OffscreenCanvas(width, height);
	const octx = off.getContext("2d", { willReadFrequently: true });
	if (!octx) throw new Error("This browser would not let the page read the image.");
	octx.drawImage(bitmap, 0, 0);
	const raw = octx.getImageData(0, 0, width, height).data;

	// Counting stops at the threshold. Tallying every colour in a 12 megapixel
	// photograph is millions of set insertions to answer a question that was
	// settled after the first few thousand.
	const tally = new Set<number>();
	for (let i = 0; i < raw.length && tally.size <= SPRITE_MAX_COLOURS; i += 4) {
		if (raw[i + 3] === 0) continue;
		tally.add((raw[i] << 16) | (raw[i + 1] << 8) | raw[i + 2]);
	}
	const distinct = tally.size;
	const countCapped = distinct > SPRITE_MAX_COLOURS;
	const kind: ShotKind = Math.max(width, height) <= SPRITE_MAX_EDGE && !countCapped ? "sprite" : "photo";

	const swatches = paletteOf(raw, kind === "sprite");

	const longest = Math.max(width, height);
	const scale =
		kind === "sprite"
			? Math.max(1, Math.floor(UPLOAD_TARGET / longest))
			: Math.min(1, UPLOAD_TARGET / longest);

	const out = new OffscreenCanvas(
		Math.max(1, Math.round(width * scale)),
		Math.max(1, Math.round(height * scale)),
	);
	const octx2 = out.getContext("2d", { willReadFrequently: true });
	if (!octx2) throw new Error("This browser would not let the page resize the image.");
	octx2.imageSmoothingEnabled = kind === "photo";
	octx2.drawImage(off, 0, 0, out.width, out.height);
	bitmap.close();

	const seen: Bitmap = {
		width: out.width,
		height: out.height,
		data: new Uint8Array(octx2.getImageData(0, 0, out.width, out.height).data),
	};

	// The thumbnail comes off the original, not off `seen`. A sprite keeps its
	// hard pixels because it is small already and only needs a whole-number
	// scale; a photo is resampled smoothly like any other photo.
	const thumbScale = kind === "sprite" ? Math.max(1, Math.floor(THUMB / longest)) : Math.min(1, THUMB / longest);
	const tiny = new OffscreenCanvas(
		Math.max(1, Math.round(width * thumbScale)),
		Math.max(1, Math.round(height * thumbScale)),
	);
	const tctx = tiny.getContext("2d", { willReadFrequently: true });
	if (!tctx) throw new Error("This browser would not let the page resize the image.");
	tctx.imageSmoothingEnabled = kind === "photo";
	tctx.drawImage(off, 0, 0, tiny.width, tiny.height);
	const thumb: Bitmap = {
		width: tiny.width,
		height: tiny.height,
		data: new Uint8Array(tctx.getImageData(0, 0, tiny.width, tiny.height).data),
	};

	const media_type = kind === "photo" ? "image/jpeg" : "image/png";
	const blob = await out.convertToBlob({ type: media_type, quality: 0.9 });
	const data = await new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(new Error("The file could not be read."));
		reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
		reader.readAsDataURL(blob);
	});

	return {
		media_type,
		data,
		width,
		height,
		palette: swatches.map(asText),
		kind,
		name: file.name,
		distinct,
		countCapped,
		scale,
		seen,
		thumb,
		swatches,
	};
}
